import { Game, Player } from "../client";
import { LEAGUE } from "./NBA";
import { urlSafeBase64Decode, urlSafeBase64Encode } from "../utils/utils";

import { GameSearchData, PlayerSearchData } from "./SearchData";

export const MAX_STACK_SIZE = 5;

export type StackedPlayerQuery = PlayerSearchData & {
  id: string;
  type: "player";
};

export type StackedGameQuery = GameSearchData & {
  id: string;
  type: "game";
};

export type StackedQuery = StackedPlayerQuery | StackedGameQuery;

export const getQueryFingerprint = (q: StackedQuery): string => {
  const params: Record<string, string> =
    q.type === "player"
      ? {
          type: q.type,
          league: q.league,
          player: String(q.player?.id || ""),
          stats: [...(q.categories || [])].sort().join(","),
          seasons: [...(q.seasons || [])].sort().join(","),
          seasonType: q.seasonType || "",
          opponent: q.oppTeamId && q.oppTeamId !== "0" ? q.oppTeamId : "",
          lastN: q.lastNGames && q.lastNGames !== "0" ? q.lastNGames : "",
          periods: [...(q.periods || [])].sort().join(","),
          dateFrom: q.dateFrom || "",
          dateTo: q.dateTo || "",
        }
      : {
          type: q.type,
          league: q.league,
          game: String(q.game?.id || ""),
          stats: [...(q.categories || [])].sort().join(","),
          singleSeason: q.singleSeason || "",
          seasonType: q.seasonType || "",
          periods: [...(q.periods || [])].sort().join(","),
          players: [...(q.players || []).map((p) => String(p.id))].sort().join(","),
        };

  return new URLSearchParams(params).toString();
};

export const areQueriesEquivalent = (a: StackedQuery, b: StackedQuery): boolean =>
  getQueryFingerprint(a) === getQueryFingerprint(b);

export type SerializedPlayerQuery = Omit<StackedPlayerQuery, "id" | "league" | "player"> & {
  player: string;
};

export type SerializedGameQuery = Omit<StackedGameQuery, "id" | "league" | "game" | "players"> & {
  game: string;
  players?: string[];
};

export type SerializedQuery = SerializedPlayerQuery | SerializedGameQuery;

export const encodeQueryStack = (queries: StackedQuery[]): string => {
  try {
    const serialized: SerializedQuery[] = queries.slice(0, MAX_STACK_SIZE).map((q) => {
      if (q.type === "player") {
        const item: SerializedPlayerQuery = {
          type: "player",
          player: q.player.id,
          categories: q.categories,
          seasons: q.seasons,
          seasonType: q.seasonType,
          oppTeamId: q.oppTeamId,
          lastNGames: q.lastNGames,
          dateFrom: q.dateFrom,
          dateTo: q.dateTo,
          periods: q.periods,
        };
        return item;
      } else {
        const item: SerializedGameQuery = {
          type: "game",
          game: q.game.id,
          categories: q.categories,
          seasonType: q.seasonType,
          singleSeason: q.singleSeason,
          players: q.players && q.players.length > 0 ? q.players.map((p) => p.id) : undefined,
          periods: q.periods,
        };
        return item;
      }
    });
    return urlSafeBase64Encode(JSON.stringify(serialized));
  } catch {
    return "";
  }
};

export const decodeQueryStack = (
  rawToken: string,
  league: LEAGUE,
  players: Player[],
  games: Game[]
): StackedQuery[] => {
  try {
    const jsonStr = urlSafeBase64Decode(rawToken);
    const parsed = JSON.parse(jsonStr) as SerializedQuery[];
    if (!Array.isArray(parsed)) return [];

    const result: StackedQuery[] = [];

    parsed.slice(0, MAX_STACK_SIZE).forEach((item) => {
      if (item.type === "player") {
        const playerId = item.player;
        if (!playerId) return;

        const matchedPlayer = players.find(
          (p) => String(p.id) === String(playerId) || p.fullName.toLowerCase() === String(playerId).toLowerCase()
        ) || {
          id: playerId,
          fullName: playerId,
          firstName: "",
          lastName: "",
          isActive: true,
        };

        const id = crypto.randomUUID();

        result.push({
          id,
          type: "player",
          league,
          player: matchedPlayer,
          categories: item.categories || [],
          seasons: item.seasons || [],
          seasonType: item.seasonType || "Regular Season",
          oppTeamId: item.oppTeamId || "0",
          lastNGames: item.lastNGames || "0",
          dateFrom: item.dateFrom || "",
          dateTo: item.dateTo || "",
          periods: item.periods || [],
        });
      } else if (item.type === "game") {
        const gameId = item.game;
        if (!gameId) return;

        const matchedGame: Game = games.find((g) => g.id === gameId) || {
          id: gameId,
          date: new Date(),
          matchup: gameId,
          seasonId: "",
          homeTeamId: "",
          homeTeam: "",
          homeAbbv: "",
          awayTeamId: "",
          awayTeam: "",
          awayAbbv: "",
          min: 240,
          videoAvailable: true,
          teamStats: {},
        };

        const rawPlayerList: string[] = item.players || [];
        const matchedPlayers = rawPlayerList
          .map((pId) => players.find((p) => String(p.id) === String(pId)))
          .filter((p): p is Player => Boolean(p));

        const id = crypto.randomUUID();

        result.push({
          id,
          type: "game",
          league,
          game: matchedGame,
          categories: item.categories || [],
          seasonType: item.seasonType || "Regular Season",
          singleSeason: item.singleSeason || (league === LEAGUE.WNBA ? "2026" : "2026-27"),
          players: matchedPlayers,
          periods: item.periods || [],
        });
      }
    });

    return result;
  } catch {
    return [];
  }
};
